/**
 * SWISS School Portal — Module Locked screen
 * ===========================================
 * Rendered in place of a page when the school's programme tier doesn't
 * include that module. Explains which SWISS tier unlocks it and lets
 * the school send an upgrade request to My Safe Spaces.
 */

import React, { useState } from 'react';
import { Lock, Check, Loader, Mail, Sparkles } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { requestSchoolUpgrade } from '../services/adminApi';
import { MODULE_LABEL, tierForModule, tierLabel, tierBlurb, isAddonOnly } from '../services/tiers';
import './ModuleLocked.css';

const fmtDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

const ModuleLocked = ({ moduleKey }) => {
  const { school } = useAuth();
  const moduleName = MODULE_LABEL[moduleKey] || 'This module';
  const requiredTier = tierForModule(moduleKey);
  const requiredLabel = tierLabel(requiredTier);
  // Licensed add-ons (WellTrack, Safe Hands, SEMH, LD) aren't in any
  // tier, so "part of SWISS Complete" would be wrong. Ask for the module.
  const addonOnly = isAddonOnly(moduleKey);

  // A request already on file (from getMe) — surfaced so the school
  // isn't nudged to ask twice.
  const existing = school?.upgradeRequest && school.upgradeRequest.status !== 'done'
    ? school.upgradeRequest
    : null;

  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(null);

  const handleSend = async () => {
    if (!school?.id) return;
    setSending(true);
    setError(null);
    try {
      await requestSchoolUpgrade(school.id, {
        requestedTier: addonOnly ? school.tier : requiredTier,
        ...(addonOnly ? { requestedModule: moduleKey } : {}),
        message: message.trim(),
      });
      setSent(true);
    } catch (e) {
      setError(e.message || 'Could not send the request. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const done = sent || !!existing;

  return (
    <div className="module-locked">
      <div className="module-locked-card">
        <div className="module-locked-badge"><Lock size={26} /></div>
        {addonOnly ? (
          <>
            <h1>{moduleName} is a licensed add-on</h1>
            <p className="module-locked-sub">
              My Safe Spaces switches {moduleName} on for each school. Your school is on{' '}
              <strong>{tierLabel(school?.tier)}</strong>; the add-on works alongside any tier.
            </p>
          </>
        ) : (
          <>
            <h1>{moduleName} is part of {requiredLabel}</h1>
            <p className="module-locked-sub">
              Your school is on <strong>{tierLabel(school?.tier)}</strong>. {tierBlurb(requiredTier)}
            </p>
          </>
        )}

        {done ? (
          <div className="module-locked-done">
            <Check size={18} />
            <div>
              <strong>Upgrade request sent.</strong>
              <span>
                {existing && !sent
                  ? existing.requestedModule
                    ? `Your request for ${MODULE_LABEL[existing.requestedModule] || 'this module'} was sent${existing.requestedAt ? ` on ${fmtDate(existing.requestedAt)}` : ''}. The SWISS team will be in touch.`
                    : `Your request to move to ${tierLabel(existing.requestedTier)} was sent${existing.requestedAt ? ` on ${fmtDate(existing.requestedAt)}` : ''}. The SWISS team will be in touch.`
                  : 'The My Safe Spaces team will reach out to you shortly.'}
              </span>
            </div>
          </div>
        ) : (
          <div className="module-locked-request">
            <p className="module-locked-cta">
              {addonOnly
                ? `Ask My Safe Spaces to switch on ${moduleName} for your school.`
                : `Contact My Safe Spaces to upgrade and unlock ${moduleName}.`}
            </p>
            <label className="module-locked-field">
              Add a note (optional)
              <textarea
                rows={3}
                value={message}
                maxLength={1000}
                placeholder={`Tell us why ${school?.shortName || 'your school'} would like ${addonOnly ? moduleName : requiredLabel}…`}
                onChange={(e) => setMessage(e.target.value)}
              />
            </label>
            {error && <div className="module-locked-error">{error}</div>}
            <button
              type="button"
              className="module-locked-btn"
              onClick={handleSend}
              disabled={sending}
            >
              {sending ? <Loader size={16} className="spin" /> : <Sparkles size={16} />}
              <span>{addonOnly ? `Request ${moduleName}` : `Request upgrade to ${requiredLabel}`}</span>
            </button>
            <a className="module-locked-mail" href="mailto:swiss@mysafespaces.org">
              <Mail size={14} /> Or email swiss@mysafespaces.org
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

export default ModuleLocked;
